import { useCallback, useEffect, useRef } from 'react';

import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';

import {
  trackBuilderTest,
  shouldTrackBuilderTestContent,
} from './builderTesting';

/**
 * Tracks Builder.io A/B content tests on content loaded while preventing duplicate tracking events during SSR and client-side hydration.
 *
 * **Behavior:**
 * - Tracks each unique content ID once per page view.
 * - Resets tracking state on pathname change.
 *
 * **Required Usage:** for page-level Builder models that render via SSR + client hydration.
 * **Optional Usage:** for section-level Builder models that only render client-side.
 *
 * @returns {Function} contentLoaded callback for BuilderComponent prop.
 *
 * @example Page-level component
 * function BuilderPage() {
 *   const trackBuilderTestOnContentLoaded = useTrackBuilderTestOnContentLoaded();
 *
 *   return (
 *     <BuilderComponent
 *       model="page"
 *       contentLoaded={trackBuilderTestOnContentLoaded}
 *     />
 *   );
 * }
 *
 * @example
 * // Section component - simpler to call trackBuilderTest directly
 * function PDPVideoCarousel() {
 *   const dispatch = useDispatch();
 *
 *   return (
 *     <BuilderComponent
 *       model="pdp-video-carousel"
 *       contentLoaded={(_, content) => trackBuilderTest(dispatch, content)}
 *     />
 *   );
 * }
 */
export function useTrackBuilderTestOnContentLoaded() {
  const dispatch = useDispatch();

  const router = useRouter();
  const pathname = router.asPath?.split('?')[0];
  const previousPathnameRef = useRef(pathname);

  const trackedIdsRef = useRef(new Set());

  useEffect(() => {
    // Clear tracked content ONLY when pathname actually changes
    if (previousPathnameRef.current !== pathname) {
      trackedIdsRef.current = new Set();
      previousPathnameRef.current = pathname;
    }
  }, [pathname]);

  return useCallback(
    (_, content) => {
      if (!content) {
        return;
      }

      if (
        !trackedIdsRef.current.has(content.id) &&
        shouldTrackBuilderTestContent(content)
      ) {
        trackedIdsRef.current.add(content.id);
        trackBuilderTest(dispatch, content);
      }
    },
    [dispatch]
  );
}
